/**
 * frontend/src/components/ThemeToggle.jsx
 * Shared SignalIQ dark/light selector.
 *
 * Works before login and after login as long as ThemeProvider wraps <App />.
 */

import React from 'react';
import { CheckCircle, Moon, Sun } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

export default function ThemeToggle({ compact = false }) {
  const { theme, setTheme, isDark } = useTheme();

  const current = theme === 'light' ? 'light' : 'dark';

  if (compact) {
    return (
      <button
        type="button"
        onClick={() => setTheme(isDark ? 'light' : 'dark')}
        aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
        title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
        style={compactButtonStyle}
      >
        {isDark ? <Moon size={15} /> : <Sun size={15} />}
      </button>
    );
  }

  return (
    <div style={wrapStyle} role="group" aria-label="Theme preference">
      <button
        type="button"
        onClick={() => setTheme('dark')}
        aria-pressed={current === 'dark'}
        style={optionStyle(current === 'dark')}
      >
        <Moon size={14} />
        <span>Dark</span>
        {current === 'dark' ? <CheckCircle size={12} style={{ marginLeft: 3 }} /> : null}
      </button>

      <button
        type="button"
        onClick={() => setTheme('light')}
        aria-pressed={current === 'light'}
        style={optionStyle(current === 'light')}
      >
        <Sun size={14} />
        <span>Light</span>
        {current === 'light' ? <CheckCircle size={12} style={{ marginLeft: 3 }} /> : null}
      </button>
    </div>
  );
}

const wrapStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: 5,
  borderRadius: 999,
  background: 'var(--bg-card, rgba(17,31,51,0.92))',
  border: '1px solid var(--border-mid, rgba(148,163,184,0.18))',
  boxShadow: '0 12px 34px rgba(15,23,42,0.16)',
  backdropFilter: 'blur(16px)',
};

const optionStyle = (active) => ({
  minHeight: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '6px 10px',
  borderRadius: 999,
  border: active ? '1px solid var(--blue, #2563eb)' : '1px solid transparent',
  background: active ? 'rgba(37,99,235,0.14)' : 'transparent',
  color: active ? 'var(--blue-lt, #3b82f6)' : 'var(--txt-mid, #7a94b8)',
  cursor: 'pointer',
  fontFamily: 'var(--font-ui, Inter, system-ui, sans-serif)',
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1,
  whiteSpace: 'nowrap',
});

const compactButtonStyle = {
  width: 36,
  height: 36,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 999,
  border: '1px solid var(--border-mid, rgba(148,163,184,0.18))',
  background: 'var(--bg-card, rgba(17,31,51,0.92))',
  color: 'var(--txt-hi, #e8f0fe)',
  cursor: 'pointer',
  boxShadow: '0 12px 34px rgba(15,23,42,0.16)',
  backdropFilter: 'blur(16px)',
};
